import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import {
  appConfigService,
  getIntegrationFields,
  missingRequired,
  parseValueForType,
  stringifyValue,
  type AppConfig,
  type AppConfigDataType,
  type IntegrationFieldSpec,
} from '../../services/app-config.service';
import IntegrationConfigEditor from './integration-config-editor';
import IntegrationAccountActions from './integration-account-actions';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import {
  descriptorFor,
  getProviderFields,
  type ProviderDescriptor,
} from '../../services/integration-config.service';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import { useAppConfigDataTypes } from './use-app-config-value-sets';

/* The platform naming standard: dot-separated PascalCase segments, e.g.
   `CCTV.DelayThreshold`. Checked here so a typo is caught before a round trip;
   the SERVER remains the authority — `app_config_keys` owns the closed field
   vocabulary for the two namespaced families, and its rejection is surfaced
   inline on this field (see `handleSave`) rather than only as a toast. */
const KEY_SHAPE = /^[A-Z][A-Za-z0-9]*(\.[A-Za-z0-9]+)+$/;

/* Real settings of the kind a production system actually carries, written in
   the platform's dot notation. The familiar spellings of these
   (`ApiSettings:Timeout`, `Logging:LogLevel`) are colon-separated because that
   is ASP.NET Core's convention; this platform separates on "." — see
   `app_config_keys.SEPARATOR` — and the server rejects anything else, so the
   examples are given the way a key must actually be typed here. */
const KEY_EXAMPLES = [
  'ConnectionStrings.Primary',
  'ApiSettings.Timeout',
  'Logging.LogLevel',
  'RetryPolicy.MaxRetries',
];

const KEY_HINT = `Platform naming standard, e.g. ${KEY_EXAMPLES.slice(0, 3).join(', ')}. `
  + 'Integration and BackgroundJobs keys use their own fixed vocabulary.';

/** The two namespaces whose field names are a fixed, closed vocabulary. */
const FIXED_VOCABULARY_NAMESPACES = ['Integration', 'BackgroundJobs'];

/** Reject a key the server would reject, and say why in the same words. */
const validateConfigKey = (key: string): string | null => {
  const trimmed = key.trim();
  if (!trimmed) return 'A configuration key is required.';
  if (!KEY_SHAPE.test(trimmed)) {
    return 'Use the platform naming standard: dot-separated segments starting with a capital, e.g. CCTV.DelayThreshold.';
  }
  const namespace = trimmed.split('.')[0];
  if (FIXED_VOCABULARY_NAMESPACES.includes(namespace)) {
    const segments = trimmed.split('.').length;
    // Integration.<Channel>.<Provider>.<Field> or Integration.<Channel>.ActiveProvider
    const shapeOk = namespace === 'Integration' ? segments === 3 || segments === 4 : segments === 2;
    if (!shapeOk) {
      return namespace === 'Integration'
        ? 'An Integration key must be Integration.<Channel>.<Provider>.<Field> or Integration.<Channel>.ActiveProvider.'
        : 'A BackgroundJobs key must be BackgroundJobs.<Field>.';
    }
  }
  return null;
};

/** Datatypes whose value is edited as multi-line JSON. */
const JSON_TYPES: AppConfigDataType[] = ['ARRAY', 'OBJECT'];

/**
 * How each datatype is typed, not merely how it is checked.
 *
 * `parseValueForType` already rejects a bad value on save, but only after the
 * operator has finished writing it. `allow` is a filter on what the field will
 * hold at all, so a numeric setting cannot accept letters in the first place;
 * it matches the value MID-TYPE, which is why "-", "" and a trailing "." pass —
 * refusing those would make a negative or a decimal impossible to type.
 */
const TYPE_INPUT: Partial<Record<AppConfigDataType, {
  placeholder: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'url';
  allow?: RegExp;
}>> = {
  STRING: { placeholder: 'e.g. Information', inputMode: 'text' },
  INTEGER: { placeholder: 'e.g. 30', inputMode: 'numeric', allow: /^-?\d*$/ },
  DECIMAL: { placeholder: 'e.g. 2.5', inputMode: 'decimal', allow: /^-?\d*\.?\d*$/ },
  URL: { placeholder: 'https://api.example.com/v1', inputMode: 'url' },
  VALUESET_REF: { placeholder: 'e.g. alertSeverity — the key of a value set' },
  ARRAY: { placeholder: '["Console", "File"]' },
  OBJECT: { placeholder: '{ "maxRetries": 3, "backoffSeconds": 2 }' },
  ENCRYPTED: { placeholder: 'Enter the credential' },
};

/** `Integration.<Channel>.<Provider>.Config` — the per-project override of a
 *  provider's connection settings. The object's shape is whatever that ONE
 *  provider reads, which differs completely between them: UltraMsg wants an
 *  instanceId, MSG91 a senderId and a route, SMTP a host and a port. Without
 *  naming them, this field is an unlabelled blob and a plausible-looking key that
 *  the provider never reads looks identical to one that works. */
/**
 * Provider settings that must NOT be offered inside the `Config` object. Every
 * plain value — instanceId, senderId, fromAddress, enabled, … — lives IN `Config`
 * now; only secrets have keys of their own, and the descriptor already marks
 * those. `fromEmail` is the deployment row's spelling of `fromAddress`, which is
 * what a project's `Config` carries.
 */
const DEDICATED_KEY_FIELDS = new Set([
  'fromEmail',
]);

/** Any `Integration.*` key — opened with the values the deployment already uses. */
const INTEGRATION_KEY = /^Integration\.[^.]+\.[^.]+(\.[^.]+)?$/;

const PROVIDER_CONFIG_KEY = /^Integration\.([^.]+)\.([^.]+)\.Config$/;

/** A titled card, matching the section wrapper every other form screen uses:
 *  a blue leading icon, then the title. */
const FormSection = ({
  title,
  icon,
  children,
}: {
  title: string;
  /** PrimeIcon class, e.g. 'pi pi-info-circle'. */
  icon: string;
  children: React.ReactNode;
}) => (
  <div className="ds-card mb-4">
    <div className="px-4 py-3 border-b border-line">
      <div className="flex items-center gap-2">
        <i className={`${icon} text-blue-600`} />
        <span className="text-sm font-medium text-ink">{title}</span>
      </div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

/** The control a datatype is edited with. Value and Default Value are the same
 *  field twice over — same datatype, same parser, same placeholders — so they
 *  share one editor rather than two copies that can drift apart. */
const ValueEditor = ({
  dataType,
  value,
  onChange,
  label,
  required,
  helperText,
  error,
  rows = 8,
  testId,
}: {
  dataType: AppConfigDataType;
  value: string | boolean;
  onChange: (next: string | boolean) => void;
  /** Floating label, so the editor matches the standard fields around it. */
  label: string;
  required?: boolean;
  helperText?: string;
  error?: string | null;
  rows?: number;
  testId: string;
}) => {
  const spec = TYPE_INPUT[dataType];

  if (dataType === 'BOOLEAN') {
    return (
      <FormSelect
        name={testId}
        label={label}
        required={required}
        value={(value as boolean) ? 'true' : 'false'}
        options={[{ label: 'True', value: 'true' }, { label: 'False', value: 'false' }]}
        onChange={(e) => onChange(e.target.value === 'true')}
        error={!!error}
        helperText={error || helperText}
      />
    );
  }

  if (JSON_TYPES.includes(dataType)) {
    return (
      <FormInput
        name={testId}
        label={label}
        required={required}
        multiline
        rows={rows}
        value={value as string}
        onChange={(e) => onChange(e.target.value)}
        placeholder={spec?.placeholder}
        error={!!error}
        helperText={error || helperText}
      />
    );
  }

  return (
    <FormInput
      name={testId}
      label={label}
      required={required}
      value={value as string}
      // A keystroke that would make the field un-parseable is refused rather
      // than accepted and reported later: for INTEGER and DECIMAL that means
      // letters never land in the box at all.
      onChange={(e) => {
        const next = e.target.value;
        if (spec?.allow && !spec.allow.test(next)) return;
        onChange(next);
      }}
      placeholder={spec?.placeholder}
      error={!!error}
      helperText={error || helperText}
    />
  );
};

const AppConfigForm = () => {
  const navigate = useAppNavigate();
  const { configKey: routeKey } = useParams<{ configKey: string }>();
  const [searchParams] = useSearchParams();
  const scopedAppId = searchParams.get('appId') || undefined;
  const isEdit = !!routeKey;
  const toast = useRef<Toast>(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<AppConfig | null>(null);

  const [configKey, setConfigKey] = useState('');
  const [description, setDescription] = useState('');
  const [dataType, setDataType] = useState<AppConfigDataType | null>(null);
  const [valueText, setValueText] = useState('');
  const [boolValue, setBoolValue] = useState<boolean>(false);
  const [rawValue, setRawValue] = useState('');
  /* Which project this configuration belongs to. There is no picker for it any
     more — the list decides, and passes it here as `?appId=`.

     The query parameter comes FIRST so that creating from the list while viewing
     another project lands the row under the project being viewed, not under
     whatever the signed-in token happens to say. Falling back the other way would
     silently file the row against the wrong project, and `appId` is immutable
     afterwards.

     `null` means the shared Default row, which only a platform administrator may
     write — so defaulting to it had an ordinary admin fill the form in and get
     the save refused with GLOBAL_FORBIDDEN. */
  const [appId, setAppId] = useState<string | null>(
    () => searchParams.get('appId') || localStorage.getItem('appId') || null);
  /** The projects this caller may file a configuration against. */
  const [projects, setProjects] = useState<AccessibleProject[]>([]);
  /* On edit the project is stated, not offered — resolved to the same
     "CODE — Name" the create dropdown shows. A row with no appId is the shared
     PLATFORM default, which is a real answer here, not a missing one. */
  const projectLabel = (() => {
    if (!appId) return 'Shared (all projects)';
    const p = projects.find((x) => x._id === appId);
    return p ? `${p.appCode} — ${p.name}` : appId;
  })();
  const [valueError, setValueError] = useState<string | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [defaultValueText, setDefaultValueText] = useState('');
  const [defaultBoolValue, setDefaultBoolValue] = useState<boolean>(false);
  const [defaultValueError, setDefaultValueError] = useState<string | null>(null);
  const [descriptors, setDescriptors] = useState<ProviderDescriptor[]>([]);
  const { options: dataTypeOptions } = useAppConfigDataTypes();

  const isEncrypted = dataType === 'ENCRYPTED';
  /** No value can be edited until its type is known — the type is what says
   *  which editor to render and how to parse what is typed into it. */
  const typeChosen = dataType !== null;
  const isJson = !!dataType && JSON_TYPES.includes(dataType);

  useBreadcrumbTitle(isEdit ? configKey : undefined);

  /** The provider whose settings this key overrides, when it is one of those keys. */
  const providerFor = useMemo(() => {
    const match = PROVIDER_CONFIG_KEY.exec(configKey.trim());
    if (!match) return null;
    const [, channel, provider] = match;
    return { channel: channel.toLowerCase(), provider: provider.toLowerCase() };
  }, [configKey]);

  /* What this integration's Config object holds, from the API. Only `…Config`
     keys have one; everything else keeps the ordinary editors. */
  const [fieldSpec, setFieldSpec] = useState<IntegrationFieldSpec | null>(null);
  const [jsonMode, setJsonMode] = useState(false);
  /* Loaded once per key, and merged UNDER whatever this row already holds, so the
     editor — fields or JSON — opens on one complete object: what the deployment
     uses, with this row's own values on top. Each property appears once. */
  const mergedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!providerFor) { setFieldSpec(null); mergedFor.current = null; return; }
    const key = configKey.trim();
    let cancelled = false;
    getIntegrationFields(providerFor.channel, providerFor.provider)
      .then((spec) => {
        if (cancelled || !spec.fields.length) return;
        setFieldSpec(spec);
        if (mergedFor.current === key || (isEdit && loading)) return;
        mergedFor.current = key;
        setValueText((current) => {
          let own: Record<string, unknown> = {};
          try {
            const parsed = JSON.parse(current || '{}');
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) own = parsed;
          } catch { /* unparseable text is left to the JSON editor to complain about */ }
          return JSON.stringify({ ...spec.startingValues, ...own }, null, 2);
        });
      })
      // No description is not an error — the JSON editor still works.
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [providerFor, configKey, isEdit, loading]);

  const useFieldEditor = !!fieldSpec && isJson && !isEncrypted;

  /** The Config object being edited. `valueText` stays the single source of truth,
   *  so saving, validation and the JSON view all keep working unchanged. */
  const configObject = useMemo(() => {
    try {
      const parsed = JSON.parse(valueText || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }, [valueText]);

  const setConfigObject = (next: Record<string, unknown>) => {
    setValueText(JSON.stringify(next, null, 2));
    setValueError(null);
  };

  const providerDescriptor = useMemo(
    () => (providerFor
      ? descriptorFor(descriptors, providerFor.channel, providerFor.provider)
      : undefined),
    [descriptors, providerFor]);

  /**
   * The shape this object is expected to hold, built from the provider's own
   * field descriptors. Values are `null` — "a key you may set", not a value.
   *
   * Null is not cosmetic. `apply_override` merges a project's `Config` OVER the
   * real provider settings and drops null and empty entries, skipping the block
   * when nothing survives. So a skeleton documents every key the adapter reads
   * and still resolves to no override at all; fill one in and only that one
   * takes effect.
   */
  const exampleObject = useMemo(() => {
    if (!providerDescriptor) return null;
    const shape: Record<string, unknown> = {};
    providerDescriptor.fields
      .filter((f) => f.block === 'config' && !f.secret
        && !DEDICATED_KEY_FIELDS.has(f.name))
      .forEach((f) => { shape[f.name] = null; });
    return Object.keys(shape).length ? shape : null;
  }, [providerDescriptor]);

  // Loaded only when the key names a provider, so an ordinary config key costs
  // no extra request. The list is small and changes only on deploy.
  useEffect(() => {
    if (!providerFor || descriptors.length > 0) return;
    let cancelled = false;
    getProviderFields()
      .then((rows) => { if (!cancelled) setDescriptors(rows); })
      // A missing description is not an error — the JSON editor still works, it
      // just cannot name the fields. Failing the form here would block an edit
      // that has nothing to do with the hint.
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [providerFor, descriptors.length]);

  const notify = (severity: 'success' | 'error', summary: string, detail: string) =>
    toast.current?.show({ severity, summary, detail, life: severity === 'error' ? 10000 : 4000 });

  /* Pre-load an Integration key from its PLATFORM default row (appId null): the
     type, the description and — unless it is a secret — the template value, so a
     project's Config opens with every property it can set and the platform's
     values already in place. Only on create, only once per key, and never over
     something already typed. */
  const preloadedKey = useRef<string | null>(null);
  const [preloadedFrom, setPreloadedFrom] = useState<string | null>(null);
  useEffect(() => {
    const key = configKey.trim();
    if (isEdit || !INTEGRATION_KEY.test(key) || preloadedKey.current === key) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const row = await appConfigService.getByKey(key, appId ?? undefined);
        if (cancelled || row.appId) return; // the project already has its own row
        preloadedKey.current = key;
        setDataType((current) => current ?? row.dataType);
        setDescription((current) => current || row.description);
        if (row.dataType !== 'ENCRYPTED' && row.dataType !== 'BOOLEAN') {
          setValueText((current) => current || stringifyValue(row.dataType, row.value));
        } else if (row.dataType === 'BOOLEAN') {
          setBoolValue(row.value === true);
        }
        setPreloadedFrom(key);
      } catch {
        // No platform row for this key — nothing to pre-load.
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [configKey, isEdit, appId]);

  useEffect(() => {
    projectsService.getAccessible()
      .then((rows) => {
        // Kept, not just sampled: the Project picker below offers these. The
        // fallback stays — a caller whose token names no project still needs an
        // appId, and a create sent unscoped is refused.
        setProjects(rows);
        if (!isEdit) setAppId((current) => current || rows[0]?._id || null);
      })
      .catch((error) => {
        // Still surfaced rather than swallowed: without an appId from either
        // source, a create would be sent unscoped and refused, and the operator
        // would have no idea why.
        notify('error', 'Error', extractErrorMessage(
          error, 'Could not determine which project this configuration belongs to'));
      });
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !routeKey) return;
    let cancelled = false;
    (async () => {
      try {
        const config = await appConfigService.getByKey(decodeURIComponent(routeKey), scopedAppId);
        if (cancelled) return;
        setExisting(config);
        setConfigKey(config.configKey);
        setDescription(config.description);
        setDataType(config.dataType);
        setAppId(config.appId);
        if (config.dataType === 'BOOLEAN') {
          setBoolValue(config.value === true);
          setDefaultBoolValue(config.defaultValue === true);
        }
        // An ENCRYPTED value comes back masked and must never be echoed into the
        // editor: submitting the mask back would be a caller supplying a value the
        // server owns, which it refuses. The field stays blank, meaning "keep the
        // current credential". `defaultValue` is masked by the same rule.
        else if (config.dataType !== 'ENCRYPTED') {
          setValueText(stringifyValue(config.dataType, config.value));
          setDefaultValueText(stringifyValue(config.dataType, config.defaultValue));
        }
      } catch (error) {
        notify('error', 'Error', extractErrorMessage(error, 'Failed to load the configuration'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, routeKey, scopedAppId]);

  const handleSave = async () => {
    setValueError(null);

    // The key is immutable once created, so it is only worth checking on create —
    // an edit posts no configKey at all.
    if (!isEdit) {
      const problem = validateConfigKey(configKey);
      if (problem) {
        // Shown inline beside the key field (it validates live as you type), so
        // no toast — the message belongs next to the key, not on the Create click.
        setKeyError(problem);
        return;
      }
    }
    if (!description.trim()) return notify('error', 'Validation', 'A description is required.');
    // Narrows `dataType` for everything below, and states the requirement the
    // empty-by-default picker creates.
    if (!dataType) return notify('error', 'Validation', 'A data type is required.');

    let value: unknown = null;
    let defaultValue: unknown = null;
    if (!isEncrypted) {
      if (dataType === 'BOOLEAN') {
        value = boolValue;
        defaultValue = defaultBoolValue;
      } else {
        const parsed = parseValueForType(dataType, valueText);
        if (!parsed.ok) {
          setValueError(parsed.error);
          return;
        }
        value = parsed.value;

        /* An integration missing a required property saves fine and then fails at
           send time, where the reason is a provider error rather than a blank box.
           Checked against the values as they stand, so a conditional one (the OAuth
           client id) is only demanded while its condition holds. */
        if (useFieldEditor && fieldSpec) {
          const absent = missingRequired(fieldSpec.fields, configObject);
          if (absent.length) {
            const problem = `Required: ${absent.map((f) => f.label).join(', ')}.`;
            setValueError(problem);
            setJsonMode(false);
            return notify('error', 'Validation', problem);
          }
        }

        // Either one will do, but not neither. The default is the last rung of
        // the resolution ladder (project row -> Default row -> defaultValue), so
        // a row with no value AND no default resolves to nothing at all — which
        // reads at runtime as "unavailable" rather than as a setting anybody can
        // see. Requiring the default outright was too strong: a row that states
        // its value has no use for one.
        if (!valueText.trim() && !defaultValueText.trim()) {
          const problem = 'Enter a value or a default value.';
          setDefaultValueError(problem);
          return notify('error', 'Validation', problem);
        }
        const parsedDefault = parseValueForType(dataType, defaultValueText);
        if (!parsedDefault.ok) {
          setDefaultValueError(parsedDefault.error);
          return;
        }
        defaultValue = parsedDefault.value;
      }
    }

    try {
      setSaving(true);
      if (isEdit && existing) {
        // `configKey`, `dataType` and `appId` are immutable, so they are not sent.
        // `value` IS sent even when null — an explicit null is how a project stops
        // overriding a setting and returns to the Default. Omitting it would keep
        // the last value, so a field the operator cleared would stay in force.
        await appConfigService.update(
          existing.configKey,
          {
            description: description.trim(),
            ...(isEncrypted
              ? (rawValue.trim() ? { rawValue: rawValue.trim() } : {})
              : { value, defaultValue }),
          },
          existing.appId ?? undefined,
        );
        notify('success', 'Saved', 'Configuration updated.');
      } else {
        await appConfigService.create({
          configKey: configKey.trim(),
          description: description.trim(),
          dataType,
          appId,
          ...(isEncrypted ? { rawValue: rawValue.trim() } : { value, defaultValue }),
        });
        notify('success', 'Created', 'Configuration created.');
      }
      navigate('/app-config');
    } catch (error) {
      const message = extractErrorMessage(error, 'Could not save the configuration');
      // `app_config_keys` owns the closed vocabulary for the Integration and
      // BackgroundJobs namespaces, so the server is the only place that knows
      // whether a field name is real. Its answer belongs under the key field,
      // not only in a toast that disappears.
      if (/\bkey\b/i.test(message)) setKeyError(message);
      notify('error', 'Error', message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <PermissionGuard jobName={JOB_NAMES.APP_CONFIG}>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-AppConfig-Form">
          {/* Header */}
          <ScreenHeader
            icon="pi pi-cog"
            title={isEdit ? 'Edit Configuration' : 'Create New Configuration'}
            subtitle={isEdit ? configKey : 'A stable key application code reads by, and the value it resolves to.'}
            onBack={() => navigate('/app-config')}
          />

          <form
            onSubmit={(e) => { e.preventDefault(); handleSave(); }}
            data-testid="AppConfigForm.Form"
          >
            {/* Identity */}
            <FormSection title="Basic Information" icon="pi pi-info-circle">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  name="configKey"
                  label="Configuration Key"
                  required
                  value={configKey}
                  onChange={(e) => {
                    const next = e.target.value;
                    setConfigKey(next);
                    // Validate live, so the naming-standard message shows inline
                    // under the field while the key is being typed — not only as
                    // a toast after Create. Empty clears it (no nagging before
                    // anything is typed).
                    setKeyError(next.trim() ? validateConfigKey(next) : null);
                  }}
                  disabled={isEdit}
                  placeholder="ApiSettings.Timeout"
                  error={!!keyError}
                  helperText={keyError
                    // Immutable, and worth saying why: application code compiled against it.
                    || (isEdit ? 'Immutable — application code reads by this key.'
                      : preloadedFrom === configKey.trim()
                        ? 'Opened with the values this deployment uses. Change what this '
                          + 'project needs; they are saved as its own.'
                        : KEY_HINT)}
                />

                {/* Which project this configuration belongs to (immutable after
                    creation, so on edit it is stated rather than offered). */}
                {isEdit ? (
                  <FormInput
                    name="appId"
                    label="Project"
                    value={projectLabel}
                    onChange={() => undefined}
                    disabled
                    helperText="Immutable — a configuration cannot change owner."
                  />
                ) : (
                  <FormSelect
                    name="appId"
                    label="Project"
                    required
                    value={appId || ''}
                    onChange={(e) => setAppId(e.target.value || null)}
                    options={projects.map((p) => ({
                      label: `${p.appCode} — ${p.name}`, value: p._id }))}
                    helperText="The project this setting belongs to. It cannot be changed afterwards."
                  />
                )}

                {/* Data type — fixed at creation; decides how the value is parsed on read. */}
                <FormSelect
                  name="dataType"
                  label="Data Type"
                  required
                  value={dataType ?? ''}
                  options={dataTypeOptions}
                  placeholder="Select Data Type"
                  onChange={(e) => {
                    setDataType((e.target.value || null) as AppConfigDataType | null);
                    // Whatever was typed was parsed against the OLD type, so it
                    // is cleared rather than reinterpreted under the new one.
                    setValueText(''); setValueError(null);
                    setBoolValue(false);
                    setDefaultValueText(''); setDefaultValueError(null);
                    setDefaultBoolValue(false);
                  }}
                  disabled={isEdit}
                  helperText={isEncrypted
                    ? 'Encrypted on save and never shown again.'
                    : (isEdit ? undefined : 'Immutable field — configure it carefully.')}
                />

                <div className="md:col-span-2">
                  <FormInput
                    name="description"
                    label="Description"
                    required
                    multiline
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What this setting controls."
                  />
                </div>
              </div>
            </FormSection>

            {/* Value */}
            <FormSection title="Value" icon="pi pi-sliders-h">
              {/* Two views of the same setting, side by side: the default it falls
                  back to on the left, the value in force on the right. Stacked for
                  the JSON types, where two editors on one row leaves neither wide
                  enough to read. */}
              {!typeChosen ? (
                <p className="text-sm text-ink-muted" data-testid="AppConfigForm.Hint.PickType">
                  Select a data type to enter a value.
                </p>
              ) : (
              <div className={`grid gap-4 ${isJson ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                {/* Default Value — the last rung of the resolution ladder (this
                    project's row -> the Default row -> this field). Omitted for
                    ENCRYPTED: `defaultValue` is stored exactly as sent, with only
                    `rawValue` routed through encryption, so a credential typed
                    here would be persisted in the clear. */}
                {!isEncrypted && (
                  <ValueEditor
                    dataType={dataType!}
                    label="Default Value"
                    helperText="Used when neither this project nor the Default row supplies one. Optional — but a row needs a value or a default."
                    value={dataType === 'BOOLEAN' ? defaultBoolValue : defaultValueText}
                    onChange={(next) => {
                      if (dataType === 'BOOLEAN') setDefaultBoolValue(next as boolean);
                      else setDefaultValueText(next as string);
                      setDefaultValueError(null);
                    }}
                    error={defaultValueError}
                    rows={6}
                    testId="AppConfigForm.Input.DefaultValue"
                  />
                )}

                {isEncrypted ? (
                  <FormInput
                    name="rawValue"
                    label="Value"
                    type="password"
                    value={rawValue}
                    onChange={(e) => setRawValue(e.target.value)}
                    placeholder={isEdit ? 'Leave blank to keep the current credential' : 'Enter the credential'}
                    /* Write-only, and blank means keep — never clear. Sending an empty
                       credential on an unrelated save is how a working token gets
                       revoked without anyone intending it. */
                    helperText="Encrypted on save and never returned. Blank keeps the stored credential."
                  />
                ) : useFieldEditor ? (
                  /* The Config object of an integration: one labelled field per
                     property the channel reads, rather than hand-written JSON.
                     The JSON editor stays one click away for anything the
                     description does not cover. */
                  <div className="sm:col-span-2 space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                        {fieldSpec?.channel} settings
                      </label>
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => setJsonMode((v) => !v)}
                        data-testid="AppConfigForm.Button.JsonMode"
                      >
                        {jsonMode ? 'Edit as fields' : 'Edit as JSON'}
                      </button>
                    </div>
                    {jsonMode ? (
                      <ValueEditor
                        dataType={dataType!}
                        label="Value (JSON)"
                        value={valueText}
                        onChange={(next) => { setValueText(next as string); setValueError(null); }}
                        error={valueError}
                        testId="AppConfigForm.Input.Value"
                      />
                    ) : (
                      <IntegrationConfigEditor
                        spec={fieldSpec!}
                        value={configObject}
                        onChange={setConfigObject}
                      />
                    )}
                    {valueError && <p className="text-xs text-red-600">{valueError}</p>}
                  </div>
                ) : (
                  <ValueEditor
                    dataType={dataType!}
                    label="Value"
                    // The only way to un-override, so it has to be discoverable.
                    helperText={isEdit && existing?.appId
                      ? 'Clear to stop overriding and fall back to the Default row.'
                      : undefined}
                    value={dataType === 'BOOLEAN' ? boolValue : valueText}
                    onChange={(next) => {
                      if (dataType === 'BOOLEAN') setBoolValue(next as boolean);
                      else setValueText(next as string);
                      setValueError(null);
                    }}
                    error={valueError}
                    testId="AppConfigForm.Input.Value"
                  />
                )}

                {/* Check the connection: email signs in, WhatsApp asks its instance
                    status. Only once the row exists — both act on what is SAVED,
                    not on what is typed. */}
                {isEdit && useFieldEditor && fieldSpec && fieldSpec.actions.length > 0 && (
                  <div className="sm:col-span-2">
                    <IntegrationAccountActions
                      channel={fieldSpec.channel}
                      actions={fieldSpec.actions}
                      appId={existing?.appId || appId || undefined}
                      authType={typeof configObject.authType === 'string'
                        ? configObject.authType : undefined}
                    />
                  </div>
                )}

                {providerFor && isJson && !useFieldEditor && (
                  <div
                    className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950"
                    data-testid="AppConfigForm.Hint.ProviderFields"
                  >
                    {providerDescriptor ? (
                      <>
                        <p className="mb-1 text-sm font-medium text-blue-900 dark:text-blue-200">
                          Keys {providerFor.channel} / {providerFor.provider} reads
                        </p>
                        <ul className="space-y-0.5 text-xs text-blue-800 dark:text-blue-300">
                          {providerDescriptor.fields
                            .filter((f) => f.block === 'config' && !f.secret)
                            .map((f) => (
                              <li key={f.name}>
                                <span className="font-mono">{f.name}</span>
                                <span className="text-blue-600 dark:text-blue-400"> — {f.label}</span>
                              </li>
                            ))}
                        </ul>
                        {exampleObject && (
                          <div className="mt-2">
                            <div className="mb-1 flex items-center justify-between gap-2">
                              <span className="text-xs font-medium text-blue-900 dark:text-blue-200">
                                Expected shape
                              </span>
                              <button
                                type="button"
                                className="text-xs text-blue-700 hover:underline dark:text-blue-300"
                                onClick={() => {
                                  setValueText(JSON.stringify(exampleObject, null, 2));
                                  setValueError(null);
                                }}
                                data-testid="AppConfigForm.Button.UseExample"
                              >
                                Insert into the editor
                              </button>
                            </div>
                            <pre className="overflow-x-auto rounded bg-white/70 p-2 font-mono text-[11px] text-blue-900 dark:bg-gray-900/60 dark:text-blue-200">
{JSON.stringify(exampleObject, null, 2)}
                            </pre>
                            {/* Worth stating, because a null-filled object looks
                                like it must do something. It does not. */}
                            <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">
                              A key left null is not an override — the provider's own
                              setting is used. Fill in only what this project changes.
                            </p>
                          </div>
                        )}
                        {/* Naming them here would invite someone to paste a credential
                            into a field that is stored as written. Overriding one is a
                            separate ENCRYPTED key. */}
                        <p className="mt-2 text-xs text-blue-700 dark:text-blue-400">
                          Anything else is ignored by this provider. Credentials are not
                          set here — they belong in the provider's own ENCRYPTED key.
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-blue-800 dark:text-blue-300">
                        No field description is published for {providerFor.channel} /{' '}
                        {providerFor.provider}, so the object is stored as written.
                      </p>
                    )}
                  </div>
                )}
              </div>
              )}
            </FormSection>

            {/* Action Buttons — footer bar styled like the other master forms. */}
            <div className="px-4 py-3 bg-canvas dark:bg-gray-900/50 border border-line rounded-lg flex justify-end gap-2">
              <Button
                type="button"
                label="Cancel"
                severity="secondary"
                outlined
                onClick={() => navigate('/app-config')}
                disabled={saving}
                size="small"
                testId="AppConfigForm.Button.Cancel"
              />
              <Button
                type="submit"
                label={saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                disabled={saving}
                size="small"
                testId="AppConfigForm.Button.Save"
              />
            </div>
          </form>
      </div>
    </PermissionGuard>
  );
};

export default AppConfigForm;
